'use client';

import { createContext, ReactNode, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { UploadFile } from "@/app/lib/generated/graphql-types";
import { fetchMedia } from "@/app/features/shared/media-browser/data-access/queries";
import { MediaBrowserMode, MediaBrowserState, MediaType, PendingUpload } from "@/app/features/shared/media-browser-v2/types/types";
import { toast } from "react-toastify";
import { StrapiMediaManager } from "@/app/lib/common/image-uploader/strapi-media-manager";

const ALL_MEDIA_TYPES: MediaType[] = ['image/*', 'video/*', 'application/pdf'];
const PAGE_SIZE = 50;
const STORAGE_KEY = 'media-browser-v2-state';

const mediaManager = new StrapiMediaManager();

export type MediaBrowserV2ContextType = {
    // State
    state: MediaBrowserState;

    // Selection
    selectedMedia: UploadFile | PendingUpload | null;
    selectedMediaList: (UploadFile | PendingUpload)[];
    setSelectedId: (id: string | null) => void;
    toggleSelection: (id: string) => void;
    clearSelection: () => void;
    isSelected: (id: string) => boolean;
    setMultiSelectMode: (enabled: boolean) => void;

    // Search & Filter
    setSearchQuery: (query: string) => void;
    setMediaTypes: (types: MediaType[]) => void;

    // Pagination
    setPage: (page: number) => void;
    setPageSize: (size: number) => void;

    // Upload
    addPendingUploads: (files: File[]) => void;
    removePendingUpload: (id: string) => void;
    uploadPendingFile: (id: string) => Promise<UploadFile | null>;
    uploadAllPending: () => Promise<void>;

    // Metadata
    updateMetadata: (id: string, metadata: { name?: string; caption?: string }) => Promise<void>;

    // Delete
    deleteMedia: (media: UploadFile) => Promise<void>;

    // Actions
    reload: (skipCache?: boolean) => Promise<void>;
    onApply?: (media: UploadFile | null) => void;
    onApplyMultiple?: (media: UploadFile[]) => void;
    onClose?: () => void;
};

const MediaBrowserV2Context = createContext<MediaBrowserV2ContextType | undefined>(undefined);

export function useMediaBrowserV2() {
    const context = useContext(MediaBrowserV2Context);
    if (!context) {
        throw new Error('useMediaBrowserV2 must be used within a MediaBrowserV2Provider');
    }
    return context;
}

export function MediaBrowserV2Provider({
    children,
    mode = 'picker',
    onApply,
    onClose,
    initialMediaTypes,
    persistState = true,
}: {
    children: ReactNode;
    mode?: MediaBrowserMode;
    onApply?: (media: UploadFile | null) => void;
    onClose?: () => void;
    initialMediaTypes?: MediaType[];
    persistState?: boolean;
}) {
    // Initialize state with persistence
    const [state, setState] = useState<MediaBrowserState>(() => {
        if (persistState && typeof window !== 'undefined') {
            const saved = localStorage.getItem(STORAGE_KEY);
            if (saved) {
                try {
                    const parsed = JSON.parse(saved);
                    return {
                        ...parsed,
                        mode,
                        items: [],
                        isLoading: true,
                        selectedId: null,
                        selectedIds: [],
                        multiSelectMode: false,
                        pendingUploads: [],
                        mediaTypes: initialMediaTypes ?? parsed.mediaTypes,
                    };
                } catch (e) {
                    console.error('Failed to parse saved state', e);
                }
            }
        }

        return {
            mode,
            items: [],
            isLoading: true,
            selectedId: null,
            selectedIds: [],
            multiSelectMode: false,
            pendingUploads: [],
            searchQuery: '',
            mediaTypes: initialMediaTypes ?? ALL_MEDIA_TYPES,
            page: 1,
            pageSize: PAGE_SIZE,
            totalPages: 0,
            totalItems: 0,
        };
    });

    // Persist state changes
    useEffect(() => {
        if (persistState && typeof window !== 'undefined') {
            const toSave = {
                searchQuery: state.searchQuery,
                mediaTypes: state.mediaTypes,
                page: state.page,
                pageSize: state.pageSize,
            };
            localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
        }
    }, [state.searchQuery, state.mediaTypes, state.page, state.pageSize, persistState]);

    // Fetch media
    const reload = useCallback(async (skipCache = false) => {
        setState(prev => ({ ...prev, isLoading: true }));

        try {
            const { data, error } = await fetchMedia({
                searchQuery: state.searchQuery,
                mediaTypes: state.mediaTypes,
                paginationArg: {
                    page: state.page,
                    pageSize: state.pageSize,
                },
                skipCache,
            });

            if (error) {
                toast.error('Fehler beim Laden der Medien');
                console.error(error);
            } else if (data) {
                setState(prev => ({
                    ...prev,
                    items: data.items,
                    totalPages: data.pagination?.pageCount || 0,
                    totalItems: data.pagination?.total || 0,
                    isLoading: false,
                }));
            }
        } catch (error) {
            toast.error('Fehler beim Laden der Medien');
            console.error(error);
            setState(prev => ({ ...prev, isLoading: false }));
        }
    }, [state.searchQuery, state.mediaTypes, state.page, state.pageSize]);

    // Reload when dependencies change
    useEffect(() => {
        reload();
    }, [reload]);

    // Selection
    const setSelectedId = useCallback((id: string | null) => {
        setState(prev => ({ ...prev, selectedId: id, selectedIds: id ? [id] : [] }));
    }, []);

    const toggleSelection = useCallback((id: string) => {
        setState(prev => {
            // If not in multi-select mode, just set single selection
            if (!prev.multiSelectMode) {
                return { ...prev, selectedId: id, selectedIds: [id] };
            }

            const isCurrentlySelected = prev.selectedIds.includes(id);
            if (isCurrentlySelected) {
                const newSelectedIds = prev.selectedIds.filter(sid => sid !== id);
                return {
                    ...prev,
                    selectedIds: newSelectedIds,
                    selectedId: newSelectedIds.length > 0 ? newSelectedIds[0] : null,
                };
            } else {
                return {
                    ...prev,
                    selectedIds: [...prev.selectedIds, id],
                    selectedId: id,
                };
            }
        });
    }, []);

    const clearSelection = useCallback(() => {
        setState(prev => ({ ...prev, selectedId: null, selectedIds: [], multiSelectMode: false }));
    }, []);

    const setMultiSelectMode = useCallback((enabled: boolean) => {
        setState(prev => {
            // When disabling multi-select, keep only the first selected item
            if (!enabled && prev.selectedIds.length > 1) {
                return {
                    ...prev,
                    multiSelectMode: false,
                    selectedIds: prev.selectedId ? [prev.selectedId] : [],
                };
            }
            return { ...prev, multiSelectMode: enabled };
        });
    }, []);

    const isSelected = useCallback((id: string) => {
        return state.selectedIds.includes(id);
    }, [state.selectedIds]);

    const selectedMedia = useMemo(() => {
        if (!state.selectedId) return null;

        // Check in items
        const item = state.items.find(i => i.documentId === state.selectedId);
        if (item) return item;

        // Check in pending uploads
        const pending = state.pendingUploads.find(p => p.id === state.selectedId);
        if (pending) return pending;

        return null;
    }, [state.selectedId, state.items, state.pendingUploads]);

    const selectedMediaList = useMemo(() => {
        const allMedia = [...state.items, ...state.pendingUploads];
        return state.selectedIds
            .map(id => {
                return allMedia.find(m => {
                    const mediaId = 'documentId' in m ? m.documentId : m.id;
                    return mediaId === id;
                });
            })
            .filter((m): m is UploadFile | PendingUpload => m !== undefined);
    }, [state.selectedIds, state.items, state.pendingUploads]);

    // Search & Filter
    const setSearchQuery = useCallback((query: string) => {
        setState(prev => ({ ...prev, searchQuery: query, page: 1 }));
    }, []);

    const setMediaTypes = useCallback((types: MediaType[]) => {
        setState(prev => ({ ...prev, mediaTypes: types, page: 1 }));
    }, []);

    // Pagination
    const setPage = useCallback((page: number) => {
        setState(prev => ({ ...prev, page }));
    }, []);

    const setPageSize = useCallback((size: number) => {
        setState(prev => ({ ...prev, pageSize: size, page: 1 }));
    }, []);

    // Upload
    const addPendingUploads = useCallback((files: File[]) => {
        const newUploads: PendingUpload[] = files.map(file => ({
            id: `pending-${Date.now()}-${Math.random()}`,
            file,
            name: file.name.replace(/\.[^/.]+$/, ""),
            caption: '',
            uploadStatus: 'pending' as const,
            uploadProgress: 0,
        }));

        setState(prev => ({
            ...prev,
            pendingUploads: [...prev.pendingUploads, ...newUploads],
        }));

        // Auto-select first if nothing selected
        setState(prev => {
            if (!prev.selectedId && newUploads.length > 0) {
                return { ...prev, selectedId: newUploads[0].id };
            }
            return prev;
        });
    }, []);

    const removePendingUpload = useCallback((id: string) => {
        setState(prev => ({
            ...prev,
            pendingUploads: prev.pendingUploads.filter(p => p.id !== id),
            selectedId: prev.selectedId === id ? null : prev.selectedId,
        }));
    }, []);

    const uploadPendingFile = useCallback(async (id: string): Promise<UploadFile | null> => {
        const pending = state.pendingUploads.find(p => p.id === id);
        if (!pending) return null;
        if (pending.uploadStatus === 'uploading') return null;

        // Update status to uploading
        setState(prev => ({
            ...prev,
            pendingUploads: prev.pendingUploads.map(p =>
                p.id === id ? { ...p, uploadStatus: 'uploading' as const } : p
            ),
        }));

        try {
            const uploadedFile = await mediaManager.upload(pending);

            // Update status to success
            setState(prev => ({
                ...prev,
                pendingUploads: prev.pendingUploads.map(p =>
                    p.id === id ? { ...p, uploadStatus: 'success' as const } : p
                ),
            }));

            // Remove after short delay
            setTimeout(() => {
                removePendingUpload(id);
                reload(true);
            }, 1000);

            toast.success('Datei erfolgreich hochgeladen');
            return uploadedFile;
        } catch (error) {
            setState(prev => ({
                ...prev,
                pendingUploads: prev.pendingUploads.map(p =>
                    p.id === id ? { ...p, uploadStatus: 'error' as const, uploadError: (error as Error).message } : p
                ),
            }));
            toast.error('Fehler beim Hochladen');
            return null;
        }
    }, [state.pendingUploads, removePendingUpload, reload]);

    const uploadAllPending = useCallback(async () => {
        const pending = state.pendingUploads.filter(p => p.uploadStatus === 'pending');
        await Promise.all(pending.map(p => uploadPendingFile(p.id)));
    }, [state.pendingUploads, uploadPendingFile]);

    // Metadata
    const updateMetadata = useCallback(async (id: string, metadata: { name?: string; caption?: string }) => {
        // Check if it's a pending upload
        const pending = state.pendingUploads.find(p => p.id === id);
        if (pending) {
            setState(prev => ({
                ...prev,
                pendingUploads: prev.pendingUploads.map(p =>
                    p.id === id ? { ...p, ...metadata } : p
                ),
            }));
            return;
        }

        // Otherwise it's an uploaded file
        const item = state.items.find(i => i.documentId === id);
        if (item) {
            try {
                await mediaManager.updateMetadata(item, metadata);
                await reload(true);
                // Don't show toast - visual feedback in editor is enough
            } catch (error) {
                // Only show toast on error
                toast.error('Fehler beim Speichern der Metadaten');
                throw error;
            }
        }
    }, [state.pendingUploads, state.items, reload]);

    // Delete
    const deleteMedia = useCallback(async (media: UploadFile) => {
        try {
            await mediaManager.delete(media);
            toast.success('Datei gelöscht');
            await reload(true);
            if (state.selectedId === media.documentId) {
                setSelectedId(null);
            }
        } catch (error) {
            toast.error('Fehler beim Löschen');
            throw error;
        }
    }, [reload, state.selectedId, setSelectedId]);

    const contextValue = useMemo(() => ({
        state,
        selectedMedia,
        selectedMediaList,
        setSelectedId,
        toggleSelection,
        clearSelection,
        isSelected,
        setMultiSelectMode,
        setSearchQuery,
        setMediaTypes,
        setPage,
        setPageSize,
        addPendingUploads,
        removePendingUpload,
        uploadPendingFile,
        uploadAllPending,
        updateMetadata,
        deleteMedia,
        reload,
        onApply,
        onClose,
    }), [
        state,
        selectedMedia,
        selectedMediaList,
        setSelectedId,
        toggleSelection,
        clearSelection,
        isSelected,
        setMultiSelectMode,
        setSearchQuery,
        setMediaTypes,
        setPage,
        setPageSize,
        addPendingUploads,
        removePendingUpload,
        uploadPendingFile,
        uploadAllPending,
        updateMetadata,
        deleteMedia,
        reload,
        onApply,
        onClose,
    ]);

    return (
        <MediaBrowserV2Context.Provider value={contextValue}>
            {children}
        </MediaBrowserV2Context.Provider>
    );
}
